import { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import Keycloak from 'keycloak-js';

import { useGlobalStore } from '@cccom/config/global-store';

// Import type augmentation
import '../axios-types';

export function tokenInterceptor(
  config: InternalAxiosRequestConfig,
  token?: string
): InternalAxiosRequestConfig {
  const apiConfig = config;
  if (apiConfig.headers && token) {
    apiConfig.headers.Authorization = `Bearer ${token}`;
  }
  return apiConfig;
}

/**
 *
 * @returns A promise that resolves when the global store is initialized.
 */
function waitForInit() {
  return new Promise<void>((resolve) => {
    if (useGlobalStore.getState().isInitialized) {
      resolve();
      return;
    }

    const unsubscribe = useGlobalStore.subscribe((state) => {
      if (state.isInitialized) {
        unsubscribe();
        resolve();
      }
    });
  });
}

export function addAuthInterceptor({
  axiosInstance,
  keycloak,
}: {
  axiosInstance: AxiosInstance;
  keycloak: Keycloak;
}) {
  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      return tokenInterceptor(config, keycloak.token);
    }
  );
}

export function addSiteInterceptor({
  axiosInstance,
}: {
  axiosInstance: AxiosInstance;
}) {
  axiosInstance.interceptors.request.use(
    async (request) => {
      // Wait for global store to be initialized
      await waitForInit();

      // Get the current site from the global store
      const { currentSite } = useGlobalStore.getState();
      const siteEntityId = currentSite?.entityId;
      if (siteEntityId) request.headers['X-SITE'] = siteEntityId;

      return request;
    },
    null,
    // Skip the interceptor if ccOptions.skipSite is true
    // This allows the request to bypass the site ID requirement
    // Useful for requests that do not require a site context
    { runWhen: (config) => !config.ccOptions?.skipSite }
  );
}

export function addESignInterceptor({
  axiosInstance,
  eSignNotes,
}: {
  axiosInstance: AxiosInstance;
  eSignNotes: string | null;
}) {
  return axiosInstance.interceptors.request.use(async (request) => {
    const eSignRequest = request?.ccOptions?.eSig;
    // BE api requires there to be a request body
    if (eSignRequest) {
      request.data = {
        ...(request.data || {}),
        eSignNotes: eSignNotes || null,
      };
    }

    return request;
  });
}
